import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const VALID_USERNAME = 'digen';
  const VALID_PASSWORD = 'whoisnext';

  useEffect(() => {
    const checkAuthStatus = () => {
      const authStatus = localStorage.getItem('emailcare_auth');
      const authTimestamp = localStorage.getItem('emailcare_auth_timestamp');
      
      if (authStatus === 'true' && authTimestamp) {
        const timestamp = parseInt(authTimestamp);
        const now = Date.now();
        const validDuration = 24 * 60 * 60 * 1000;
        
        if (now - timestamp < validDuration) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('emailcare_auth');
          localStorage.removeItem('emailcare_auth_timestamp');
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (username === VALID_USERNAME && password === VALID_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('emailcare_auth', 'true');
      localStorage.setItem('emailcare_auth_timestamp', Date.now().toString());
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('emailcare_auth');
    localStorage.removeItem('emailcare_auth_timestamp');
  };

  const value: AuthContextType = {
    isAuthenticated,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
